import { useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/PageHeader";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { AlertTriangle, Plus, Check, X, Trash2, Loader2, ShieldCheck } from "lucide-react";
import { toast } from "sonner";

const GRAV = { leve: "Leve", media: "Média", grave: "Grave" };
const statusBadge = (s) =>
  s === "validada" ? "bg-emerald-500/20 text-emerald-300 border-emerald-500/40"
  : s === "rejeitada" ? "bg-red-500/20 text-red-300 border-red-500/40"
  : "bg-orange-500/20 text-orange-300 border-orange-500/40";
const statusLabel = { pendente: "Pendente", validada: "Validada", rejeitada: "Rejeitada" };

const emptyForm = () => ({ turma_id: "", aluno_id: "", comportamento: "", descricao: "", gravidade: "media", valor_sugerido: 1 });

export default function Ocorrencias() {
  const { isAdmin } = useAuth();
  const [items, setItems] = useState([]);
  const [turmas, setTurmas] = useState([]);
  const [alunos, setAlunos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dlg, setDlg] = useState(false);
  const [form, setForm] = useState(emptyForm());
  const [saving, setSaving] = useState(false);

  const load = () => {
    setLoading(true);
    api.get("/pratica/ocorrencias").then((r) => setItems(r.data)).finally(() => setLoading(false));
  };
  useEffect(() => { load(); api.get("/turmas").then((r) => setTurmas(r.data)); }, []);

  const pickTurma = (tid) => {
    setForm((f) => ({ ...f, turma_id: tid, aluno_id: "" }));
    api.get(`/alunos?turma_id=${tid}`).then((r) => setAlunos(r.data));
  };

  const salvar = async () => {
    if (!form.turma_id || !form.aluno_id || !form.comportamento.trim()) { toast.error("Preencha turma, aluno e comportamento"); return; }
    const val = Math.max(1, Math.min(5, Number(form.valor_sugerido) || 1));
    setSaving(true);
    try {
      await api.post("/pratica/ocorrencias", { ...form, valor_sugerido: val });
      toast.success("Ocorrência registrada");
      setDlg(false); setForm(emptyForm()); load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const validar = async (id, status) => {
    try {
      await api.post(`/pratica/ocorrencias/${id}/validar`, { status });
      toast.success(status === "validada" ? "Ocorrência validada" : "Ocorrência rejeitada");
      load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const excluir = async (id, status) => {
    if (status === "validada" && !isAdmin) { toast.error("Apenas administradores podem excluir ocorrências validadas"); return; }
    if (!window.confirm("Excluir esta ocorrência?")) return;
    try { await api.delete(`/pratica/ocorrencias/${id}`); load(); } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  return (
    <div>
      <PageHeader title="Ocorrências / Penalidades" subtitle="Registro separado dos critérios · não desconta a média automaticamente (aguardando regra oficial)">
        <Button data-testid="oc-nova-btn" onClick={() => { setForm(emptyForm()); setAlunos([]); setDlg(true); }}>
          <Plus className="h-4 w-4 mr-2" /> Nova ocorrência
        </Button>
      </PageHeader>

      {loading ? <div className="flex justify-center py-16"><Loader2 className="h-7 w-7 animate-spin text-primary" /></div>
        : !items.length ? (
          <Card className="p-8 text-center bg-card border-border">
            <AlertTriangle className="h-10 w-10 mx-auto text-muted-foreground mb-3" />
            <p className="text-muted-foreground text-sm">Nenhuma ocorrência registrada.</p>
          </Card>
        ) : (
          <div className="space-y-3">
            {items.map((o) => (
              <Card key={o.id} data-testid={`oc-card-${o.id}`} className="p-4 bg-card border-border">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-semibold">{o.aluno_nome}</span>
                      <Badge className={statusBadge(o.status)} data-testid={`oc-status-${o.id}`}>{statusLabel[o.status]}</Badge>
                      <Badge className="bg-secondary text-secondary-foreground border-border">{GRAV[o.gravidade] || o.gravidade}</Badge>
                      <Badge className="bg-orange-500/15 text-orange-300 border-orange-500/30">−{o.status === "validada" ? o.valor_final : o.valor_sugerido} pts</Badge>
                    </div>
                    <div className="font-medium mt-1">{o.comportamento}</div>
                    {o.descricao && <p className="text-sm text-muted-foreground mt-0.5">{o.descricao}</p>}
                    <p className="text-[11px] text-muted-foreground font-mono mt-1">
                      Registrado por {o.registrado_por_nome}
                      {o.validado_por_nome ? ` · validado por ${o.validado_por_nome}` : ""}
                    </p>
                  </div>
                  <div className="flex gap-1.5 shrink-0">
                    {isAdmin && o.status === "pendente" && (
                      <>
                        <Button size="sm" variant="secondary" data-testid={`oc-validar-${o.id}`} onClick={() => validar(o.id, "validada")}><Check className="h-4 w-4" /></Button>
                        <Button size="sm" variant="secondary" data-testid={`oc-rejeitar-${o.id}`} onClick={() => validar(o.id, "rejeitada")}><X className="h-4 w-4" /></Button>
                      </>
                    )}
                    <Button size="sm" variant="ghost" data-testid={`oc-excluir-${o.id}`} onClick={() => excluir(o.id, o.status)}><Trash2 className="h-4 w-4 text-muted-foreground hover:text-red-400" /></Button>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        )}

      <Dialog open={dlg} onOpenChange={setDlg}>
        <DialogContent className="bg-card border-border max-w-lg">
          <DialogHeader><DialogTitle className="font-heading uppercase tracking-tight">Nova ocorrência</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Turma</Label>
              <Select value={form.turma_id} onValueChange={pickTurma}>
                <SelectTrigger data-testid="oc-turma"><SelectValue placeholder="Selecione a turma" /></SelectTrigger>
                <SelectContent className="bg-popover border-border">{turmas.map((t) => <SelectItem key={t.id} value={t.id}>{t.nome}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>Aluno</Label>
              <Select value={form.aluno_id} onValueChange={(v) => setForm({ ...form, aluno_id: v })} disabled={!form.turma_id}>
                <SelectTrigger data-testid="oc-aluno"><SelectValue placeholder="Selecione o aluno" /></SelectTrigger>
                <SelectContent className="bg-popover border-border">{alunos.map((a) => <SelectItem key={a.id} value={a.id}>{a.nome}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Comportamento observado</Label><Input data-testid="oc-comportamento" value={form.comportamento} onChange={(e) => setForm({ ...form, comportamento: e.target.value })} placeholder="Ex.: Uso de linguagem inadequada" /></div>
            <div><Label>Descrição</Label><Textarea data-testid="oc-descricao" value={form.descricao} onChange={(e) => setForm({ ...form, descricao: e.target.value })} placeholder="Detalhe o ocorrido..." /></div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <Label>Gravidade</Label>
                <Select value={form.gravidade} onValueChange={(v) => setForm({ ...form, gravidade: v })}>
                  <SelectTrigger data-testid="oc-gravidade"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-popover border-border">{Object.entries(GRAV).map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label>Pontuação sugerida (1-5)</Label>
                <Input data-testid="oc-valor" type="number" min={1} max={5} value={form.valor_sugerido} onChange={(e) => setForm({ ...form, valor_sugerido: e.target.value })} />
              </div>
            </div>
            <div className="flex items-start gap-2 text-xs text-muted-foreground bg-secondary/50 rounded-md p-2.5">
              <ShieldCheck className="h-4 w-4 shrink-0 mt-0.5 text-primary" />
              A penalidade só é considerada após validação da supervisão/coordenação e não desconta a média automaticamente.
            </div>
          </div>
          <DialogFooter>
            <Button variant="secondary" onClick={() => setDlg(false)}>Cancelar</Button>
            <Button data-testid="oc-salvar" onClick={salvar} disabled={saving}>{saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}Registrar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
